import { ModuleWithError } from './types';

export class BasicError extends Error {
  isSystemError: boolean = true;

  constructor(msg: string) {
    super(`[SYSTEM]${msg}`);
    Object.setPrototypeOf(this, BasicError.prototype);
  }
}

export class BasicSystemError extends BasicError {
  constructor(private msg: string) {
    super(` ${msg}`);
    Object.setPrototypeOf(this, BasicSystemError.prototype);
  }

  getHelp(): string {
    return this.msg;
  }
}

export class MissingModule<ModuleName extends string, K extends ModuleName> extends BasicError {
  constructor(private moduleName: K) {
    super(` Missing module '${moduleName}'`);
    Object.setPrototypeOf(this, MissingModule.prototype);
  }

  getMissingModuleName(): K {
    return this.moduleName;
  }

  getHelp(): string {
    return [``].join('\n');
  }
}

export class InvalidSchema extends BasicError {
  constructor(msg: string) {
    super(` Invalid Schema: ${msg}`);
    Object.setPrototypeOf(this, InvalidSchema.prototype);
  }

  getHelp(): string {
    return [``].join('\n');
  }
}

export class SystemAlreadyBooted extends BasicError {
  constructor() {
    super(` System already booted !`);
    Object.setPrototypeOf(this, SystemAlreadyBooted.prototype);
  }

  getHelp(): string {
    return [`System.boot have already been called.`, `You can't boot System twice ¯\\_(ツ)_/¯`].join('\n');
  }
}

export class WindowSystemAlreadyDefined extends BasicError {
  constructor() {
    super(` window.System is already defined`);
    Object.setPrototypeOf(this, WindowSystemAlreadyDefined.prototype);
  }

  getHelp(): string {
    return [`System need the variable window.System to be undefined`].join('\n');
  }
}

export class InvalidStateShape extends BasicError {
  constructor(private details: string, private path: string) {
    super(` Invalid state shape in ${path} : ${details}`);
    Object.setPrototypeOf(this, InvalidStateShape.prototype);
  }

  getHelp(): string {
    return [
      `The State of System is invalid.`,
      `The problem is most likely in ${this.path}`,
      `${this.details}`,
      `Note that the message and path above are set in the error and might be wrong...`,
      `Also, invalid state is probably a symptom of another problem...`,
    ].join('\n');
  }
}

export class AlreadyFetchedBundle<BundleName extends string, B extends BundleName> extends BasicError {
  constructor(bundleName: B) {
    super(` Can't fetch an already fetched bundle : "${bundleName}" !`);
    Object.setPrototypeOf(this, AlreadyFetchedBundle.prototype);
  }
}

export class AlreadyParsedBundle<BundleName extends string, B extends BundleName> extends BasicError {
  constructor(bundleName: B) {
    super(` Can't parse an already parsed bundle : "${bundleName}" !`);
    Object.setPrototypeOf(this, AlreadyParsedBundle.prototype);
  }
}

export class AlreadyLoadedModule<ModuleName extends string, M extends ModuleName> extends BasicError {
  constructor(moduleName: M) {
    super(` Can't load an already loaded module : "${moduleName}" !`);
    Object.setPrototypeOf(this, AlreadyLoadedModule.prototype);
  }
}

export class NotFoundBundleByResolver extends BasicError {
  constructor(private bundleName: string) {
    super(` Can't find ${bundleName} bundle !`);
    Object.setPrototypeOf(this, NotFoundBundleByResolver.prototype);
  }

  getHelp(): string {
    return [
      `Resolver didn't found the bundle "${this.bundleName}"`,
      `Most likely the app was deploy but the cache return the old manifest.json`,
      `If it's not the cache, make sure the bundle is declare in package.json and in the manifest.json`,
    ].join('\n');
  }
}

export class CircularDependencies<ModuleName extends string> extends BasicError {
  constructor(private path: Array<ModuleWithError<ModuleName>> | null) {
    super(
      ` Circular Dependencies : ${
        path ? [path[path.length - 1].name, ...path.map(p => p.name)].reverse().join(' > ') : 'missingModule'
      }`
    );
    Object.setPrototypeOf(this, CircularDependencies.prototype);
  }

  getHelp(): string {
    const path = this.path;
    let detail = `missingModule`;
    const allStack: Array<string> = [];
    if (path) {
      path.forEach((item, index) => {
        const next = path[(index + 1) % path.length].name;
        allStack.push(next + ' depend on ' + item.name);
        // console.info(next + ' depend on ' + item.name);
        const stack = (item.error.stack || '')
          .split('\n')
          .filter(line => line.match(/src\/module/))
          .map(line => line.replace(/Object\.\./, '').replace(/\(<anonymous>:[0-9]+:[0-9]+\)/, ''))
          .join('\n');
        allStack.push(stack);
      });
      detail = [path[path.length - 1].name, ...path.map(p => p.name)].reverse().join(' > ');
    }
    return [`System detect a circular dependency : ${detail}`, `Here is the detail stack :`, ...allStack].join('\n');
  }
}

export class InfiniteLoopInDependenciesResolution<ModuleName extends string> extends BasicError {
  constructor(private moduleName: ModuleName, private deps: Array<ModuleName>) {
    super(` Infinite loop in dependencies resolution from module ${moduleName} !`);
    Object.setPrototypeOf(this, InfiniteLoopInDependenciesResolution.prototype);
  }

  getHelp(): string {
    return [
      `The infinite loop safe count was reach.`,
      `You either have a dependency tree of 500 items or the algo is shitty.`,
      `Note that the algo should handle recursive loop.`,
      `The limit was reach while resolving the module ${this.moduleName}`,
      `with these dependencies : [${this.deps.join(', ')}]`,
    ].join('\n');
  }
}

export class InfiniteLoopInCircularDependenciesPathResolution<ModuleName extends string> extends BasicError {
  constructor(private moduleName: ModuleName) {
    super(` Infinite loop while trying to find recusrive path of ${moduleName} !`);
    Object.setPrototypeOf(this, InfiniteLoopInCircularDependenciesPathResolution.prototype);
  }

  getHelp(): string {
    return [
      `The infinite loop safe count was reach while trying to find the recursive path of module ${this.moduleName}`,
      `Note that this feature is for debug only, it just find where exactly is the recusive import to make the error easier to debug.`,
    ].join('\n');
  }
}

export class CannotSetBundleExport extends BasicError {
  constructor(private moduleName: string) {
    super(` : Can't assign a module export of "${moduleName}" !`);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, CannotSetBundleExport.prototype);
  }

  getHelp(): string {
    return [`You tried to assign the output of the module "${this.moduleName}"`].join('\n');
  }
}

export class ModuleError<ModuleName extends string> extends BasicError {
  constructor(private moduleName: ModuleName, private subError: any) {
    super(` : Error in module "${moduleName}" !`);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ModuleError.prototype);
  }

  getHelp(): string {
    return [`Error in module "${this.moduleName}" !`].join('\n');
  }

  getModuleName(): ModuleName {
    return this.moduleName;
  }

  getModuleError(): any {
    return this.subError;
  }
}



// WEBPACK FOOTER //
// ./node_modules/front-system/engine/errors.ts